import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import { BOSS_RELIC_NOTES } from './boss';
import { RELICS } from './relics';
import { GAME_GUIDE_SECTIONS } from './gameGuideContent';
import { useCoreboundHelp } from './CoreboundHelp';
import './GameGuide.css';
import { usePopupTransition } from './popupTransition';

const GameGuide = ({
  relics = [],
  initialQuery = '',
  initialTopic = 'start',
  onClose,
  returnFocusRef,
}) => {
  const { close, popupClass } = usePopupTransition(onClose);
  const [query, setQuery] = useState(initialQuery);
  const [topic, setTopic] = useState(initialTopic);
  const { enabled, setTipsEnabled, resetTips } = useCoreboundHelp();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const bodyRef = useRef(null);
  const search = query.trim().toLowerCase();
  const matches = (text) => text.toLowerCase().includes(search);
  const sections = search
    ? GAME_GUIDE_SECTIONS.map((section) => ({
        ...section,
        entries: matches(`${section.title} ${section.intro}`)
          ? section.entries
          : section.entries.filter((entry) =>
              matches(`${entry.title} ${entry.body} ${entry.example || ''}`)
            ),
      })).filter((section) => section.entries.length)
    : GAME_GUIDE_SECTIONS.filter((section) => section.id === topic);
  const relicMatches = Object.values(RELICS).filter(
    (relic) =>
      !relic.deprecated &&
      matches(
        `${relic.name} ${relic.description} ${BOSS_RELIC_NOTES[relic.id]}`
      )
  );

  useEffect(() => {
    const previousFocus = document.activeElement;
    const fallbackFocus = returnFocusRef?.current;
    const releaseScroll = lockBodyScroll();
    const dialog = dialogRef.current;
    if (dialog.showModal) dialog.showModal();
    else dialog.setAttribute('open', '');
    closeRef.current?.focus();
    return () => {
      releaseScroll();
      const target = previousFocus?.isConnected ? previousFocus : fallbackFocus;
      target?.focus?.({ preventScroll: true });
    };
  }, [returnFocusRef]);

  useEffect(() => {
    bodyRef.current.scrollTop = 0;
  }, [topic, query]);

  const onKeyDown = (event) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
    if (event.key !== 'Tab') return;
    const items = dialogRef.current.querySelectorAll(
      'button, input, summary, [tabindex="0"]'
    );
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const renderRelic = (relic) => (
    <div key={relic.id}>
      <dt>
        {relic.name}{' '}
        <span
          className={`coreboundRarity-${(
            relic.category || relic.rarity
          ).toLowerCase()}`}
        >
          {relic.category || relic.rarity}
          {relics.includes(relic.id) ? ' · OWNED' : ''}
        </span>
      </dt>
      <dd>{relic.description}</dd>
      <dd>
        <b>Core battle:</b> {BOSS_RELIC_NOTES[relic.id]}
      </dd>
    </div>
  );

  return createPortal(
    <dialog
      ref={dialogRef}
      className={`coreboundGuideDialog ${popupClass}`}
      aria-modal="true"
      aria-labelledby="core-guide-title"
      onKeyDown={onKeyDown}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          close();
      }}
    >
      <header className="coreboundGuideHeader">
        <div>
          <span>COREBOUND / INFORMATION</span>
          <h2 id="core-guide-title">
            {initialTopic === 'basics' ? 'Basics' : 'How to play'}
          </h2>
        </div>
        <button ref={closeRef} onClick={close} aria-label="Close game guide">
          ×
        </button>
      </header>
      <div className="coreboundGuideSearch">
        <label htmlFor="core-guide-search">Find a rule or relic</label>
        <input
          id="core-guide-search"
          type="search"
          placeholder="Try attacks, shields, or charge"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <nav className="coreboundGuideTopics" aria-label="Guide topics">
        {GAME_GUIDE_SECTIONS.map((section) => (
          <button
            key={section.id}
            aria-pressed={!search && topic === section.id}
            onClick={() => {
              setQuery('');
              setTopic(section.id);
            }}
          >
            {section.title}
          </button>
        ))}
      </nav>
      <div
        ref={bodyRef}
        className="coreboundGuideBody"
        tabIndex={0}
        role="region"
        aria-label="Corebound rules"
      >
        {search && (
          <p className="coreboundGuideSearchStatus" role="status">
            {sections.length || relicMatches.length
              ? `Results for “${query.trim()}”`
              : `No rules or relics match “${query.trim()}”. Try a shorter term or choose a topic above.`}
          </p>
        )}
        {sections.map((section) => (
          <section
            key={section.id}
            aria-labelledby={`core-guide-${section.id}`}
          >
            <h3 id={`core-guide-${section.id}`}>{section.title}</h3>
            <p className="coreboundGuideIntro">{section.intro}</p>
            {section.id === 'basics' && (
              <div className="coreboundGuideTipSettings">
                <label>
                  <input
                    type="checkbox"
                    checked={!enabled}
                    onChange={(event) => setTipsEnabled(!event.target.checked)}
                  />
                  Skip tips
                </label>
                <p>
                  Hide contextual tips across runs. Uncheck to allow tips you
                  have not dismissed, or show them all again.
                </p>
                <button type="button" onClick={resetTips}>
                  Show all tips again
                </button>
              </div>
            )}
            {section.entries.map((entry) => (
              <article key={entry.title}>
                <h4>{entry.title}</h4>
                <p>{entry.body}</p>
                {entry.diagram && (
                  <img
                    className="coreboundGuideDiagram"
                    src={`/images/corebound-basics/${entry.diagram}.webp`}
                    alt={entry.diagramAlt}
                    width={720}
                    height={240}
                  />
                )}
                {entry.example && (
                  <p className="coreboundGuideWorkedExample">
                    <b>Example:</b> {entry.example}
                  </p>
                )}
              </article>
            ))}
          </section>
        ))}
        {!search && topic === 'relics' && (
          <section aria-label="Relic reference">
            <h4>Your relics</h4>
            {relics.length ? (
              <dl
                className="coreboundGuideRelics"
                aria-label="Your relic details"
              >
                {relics
                  .map((id) => RELICS[id])
                  .filter(Boolean)
                  .map(renderRelic)}
              </dl>
            ) : (
              <p>Your acquired relics will appear here during a run.</p>
            )}
            <details>
              <summary>
                Browse all{' '}
                {
                  Object.values(RELICS).filter((relic) => !relic.deprecated)
                    .length
                }{' '}
                relics
              </summary>
              <dl className="coreboundGuideRelics">
                {Object.values(RELICS)
                  .filter((relic) => !relic.deprecated)
                  .map(renderRelic)}
              </dl>
            </details>
          </section>
        )}
        {search && relicMatches.length > 0 && (
          <section aria-label="Matching relics">
            <h3>Matching relics</h3>
            <dl className="coreboundGuideRelics">
              {relicMatches.map(renderRelic)}
            </dl>
          </section>
        )}
      </div>
    </dialog>,
    document.body
  );
};

export default GameGuide;
